/** @jsxImportSource @emotion/react */
'use memo'

import React, { useState } from 'react'
import css from '@emotion/css/macro'
import { EditOutlined, UsergroupAddOutlined } from '@ant-design/icons'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import appOrigin from '@sportninja/common/constants/appOrigin'
import ShareIcon from 'src/components/icons/ShareIcon'
import ShareModal from 'src/components/ShareModal'
import Button from 'src/components/ts-components/Button'
import { ProfilePlayer } from '../types'

interface Props {
  onEditPlayer: () => void
  onOpenFamily: () => void
  player: ProfilePlayer | null
  /**
   * Whether the current user is PRIMARY (admin) for this player. Only a primary
   * may edit the player's profile, so the Edit Player button is hidden
   * otherwise. Defaults to false.
   */
  canEdit?: boolean
}

const ProfilePlayerActions: React.FC<Props> = ({
  onEditPlayer,
  onOpenFamily,
  player,
  canEdit = false,
}) => {
  const [shareOpen, setShareOpen] = useState(false)

  const playerName = player
    ? `${player.name_first ?? ''} ${player.name_last ?? ''}`.trim() || 'Player'
    : 'Player'
  const shareUrl = player ? `${appOrigin}/player/${player.id}` : ''

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      `}
    >
      {/* Edit Player — only a primary (admin) for this player may edit. */}
      {canEdit ? (
        <Button
          text={capitalizeWords('Edit Player')}
          variant='primary'
          size='compact'
          onClick={onEditPlayer}
          icon={<EditOutlined />}
        />
      ) : null}

      <Button
        text={capitalizeWords('Family Management')}
        variant='outlined'
        size='compact'
        onClick={onOpenFamily}
        disabled={!player}
        icon={<UsergroupAddOutlined />}
      />

      <Button
        text={capitalizeWords('Share Player')}
        variant='outlined'
        size='compact'
        iconOnly
        onClick={() => setShareOpen(true)}
        disabled={!player}
        icon={<ShareIcon size={16} />}
      />

      {/* Overflow (⋮) menu hidden until its actions are defined. To re-enable,
          add a MoreOutlined import + another iconOnly compact outlined
          Button. */}

      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        entityType='player'
        entityName={playerName}
        shareUrl={shareUrl}
      />
    </div>
  )
}

export default ProfilePlayerActions
